import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { businessModelsApi, categoriesApi } from '@/services/api';
import Button from '@/components/ui/Button';
import { 
  CogIcon, 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface BusinessModel {
  id: number;
  name: string;
  description?: string;
  model_type: 'traditional' | 'pin_based' | 'hybrid';
  is_active: boolean;
  is_default: boolean;
  configuration: Record<string, any>;
  created_at: string;
}

interface CommissionRule {
  id: number;
  business_model_id: number;
  rule_type: 'global' | 'shop_type' | 'category' | 'shop_specific';
  shop_type?: string;
  category_id?: number;
  shop_id?: number;
  commission_percentage: number;
  prepaid_commission?: number;
  cod_commission?: number;
  priority: number;
  is_active: boolean;
}

interface Category {
  id: number;
  name: string;
  description?: string;
}

const BusinessModelManagementPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [businessModels, setBusinessModels] = useState<BusinessModel[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedModel, setSelectedModel] = useState<BusinessModel | null>(null);
  const [commissionRules, setCommissionRules] = useState<CommissionRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCategoryLinkModal, setShowCategoryLinkModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);

  useEffect(() => {
    loadBusinessModels();
    loadCategories();
  }, []);

  useEffect(() => {
    if (selectedModel) {
      loadCommissionRules(selectedModel.id);
    }
  }, [selectedModel]);

  const loadBusinessModels = async () => {
    try {
      setLoading(true);
      const response = await businessModelsApi.getAll();
      setBusinessModels(response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load business models');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await categoriesApi.getAll();
      setCategories(response.data);
    } catch (error: any) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadCommissionRules = async (businessModelId: number) => {
    try {
      const response = await businessModelsApi.getCommissionRules(businessModelId);
      setCommissionRules(response.data);
    } catch (error: any) {
      console.error('Failed to load commission rules:', error);
    }
  };

  const handleSetDefault = async (modelId: number) => {
    try {
      await businessModelsApi.setDefault(modelId);
      toast.success('Default business model updated');
      loadBusinessModels();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to set default model');
    }
  };

  const handleDelete = async (modelId: number) => {
    if (!confirm('Are you sure you want to delete this business model?')) return;
    
    try {
      await businessModelsApi.delete(modelId);
      toast.success('Business model deleted');
      loadBusinessModels();
      if (selectedModel?.id === modelId) {
        setSelectedModel(null);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete business model');
    }
  };

  const handleLinkCategory = async (categoryId: number, businessModelId: number) => {
    try {
      await businessModelsApi.linkCategoryBusinessModel(categoryId, {
        business_model_id: businessModelId,
        is_enabled: true,
        is_required: false,
        priority: 0
      });
      toast.success('Category linked to business model');
      setShowCategoryLinkModal(false);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to link category');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CogIcon className="h-8 w-8 text-gray-500 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Business Model Management</h1>
                <p className="text-gray-600 mt-2">Configure business models, commission rules, and category-specific settings</p>
              </div>
            </div>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Create Business Model
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Business Models List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 border-b">
                <h2 className="text-lg font-semibold text-gray-900">Business Models</h2>
              </div>
              <div className="divide-y">
                {loading ? (
                  <div className="p-4 text-center text-gray-500">Loading...</div>
                ) : businessModels.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">No business models found</div>
                ) : (
                  businessModels.map((model) => (
                    <div
                      key={model.id}
                      className={`p-4 cursor-pointer hover:bg-gray-50 ${
                        selectedModel?.id === model.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                      }`}
                      onClick={() => setSelectedModel(model)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <h3 className="font-semibold text-gray-900">{model.name}</h3>
                            {model.is_default && (
                              <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                                Default
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{model.model_type}</p>
                          <p className="text-xs text-gray-500 mt-1">{model.description}</p>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          {model.is_default ? (
                            <CheckCircleIcon className="h-5 w-5 text-green-500" />
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSetDefault(model.id);
                              }}
                              className="text-gray-400 hover:text-blue-500"
                              title="Set as default"
                            >
                              <XCircleIcon className="h-5 w-5" />
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(model.id);
                            }}
                            className="text-gray-400 hover:text-red-500"
                            title="Delete"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Details Panel */}
          <div className="lg:col-span-2">
            {selectedModel ? (
              <div className="bg-white rounded-lg shadow-sm border">
                <div className="p-6 border-b">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{selectedModel.name}</h2>
                      <p className="text-gray-600 mt-1">{selectedModel.description}</p>
                    </div>
                    <Button
                      onClick={() => setShowCategoryLinkModal(true)}
                      className="flex items-center"
                    >
                      <PlusIcon className="h-5 w-5 mr-2" />
                      Link Category
                    </Button>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  {/* Model Info */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Model Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Type</label>
                        <p className="mt-1 text-sm text-gray-900 capitalize">{selectedModel.model_type}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Status</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {selectedModel.is_active ? 'Active' : 'Inactive'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Commission Rules */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Commission Rules</h3>
                      <Button size="sm">Add Rule</Button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scope</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">COD</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {commissionRules.map((rule) => (
                            <tr key={rule.id}>
                              <td className="px-4 py-3 text-sm text-gray-900 capitalize">{rule.rule_type}</td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {rule.category_id ? `Category ${rule.category_id}` : 
                                 rule.shop_type ? rule.shop_type : 
                                 rule.shop_id ? `Shop ${rule.shop_id}` : 'Global'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">{rule.commission_percentage}%</td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {rule.cod_commission ? `${rule.cod_commission}%` : '-'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">{rule.priority}</td>
                              <td className="px-4 py-3 text-sm">
                                <button className="text-blue-600 hover:text-blue-800 mr-3">
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                                <button className="text-red-600 hover:text-red-800">
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
                <CogIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Business Model</h3>
                <p className="text-gray-600">Choose a business model from the list to view and manage its settings</p>
              </div>
            )}
          </div>
        </div>

        {/* Category Link Modal */}
        {showCategoryLinkModal && selectedModel && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Link Category to Business Model</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={selectedCategory || ''}
                    onChange={(e) => setSelectedCategory(Number(e.target.value))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="">Select a category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowCategoryLinkModal(false);
                      setSelectedCategory(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      if (selectedCategory) {
                        handleLinkCategory(selectedCategory, selectedModel.id);
                      }
                    }}
                    disabled={!selectedCategory}
                  >
                    Link Category
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessModelManagementPage;

